import type { WizardStepProps } from '../../../../types/builder.types'
import { getAllFields } from '../../../../config/builder-schemas'
import { useDataSources } from '../../../../hooks/useDataSources'
import { TableFieldsModal } from '../TableFieldsModal'
import { useMemo, useState } from 'react'
import {
  fieldsFromCustomQuery,
  getCustomQueryConfigSignature,
} from '../../../../utils/custom-query-builder-fields'

export function ChartConfiguration({ config, onChange, onNext, onPrev }: WizardStepProps) { 
  const { dataSources } = useDataSources()
  const [showTableFields, setShowTableFields] = useState(false)

  
  

  const actualDataSource = config.dataSource === "custom_query" 
  ? config.customQuery?.config?.baseTable 
  : config.dataSource;

  //config.dataSource = actualDataSource;

  
  

  const cqSig = useMemo(
    () => getCustomQueryConfigSignature(config.customQuery),
    [config.customQuery]
  )

  const allFields = useMemo(() => {
    if (!config.dataSource) return []

    if (
      (config.dataSource === 'custom_query' ||
        (config.dataSource?.includes('ckan_data_generic') && config.customQuery)) &&
      config.customQuery?.config?.selectedFields?.length
    ) {
      return fieldsFromCustomQuery(config.customQuery)
    }

    const dynamicSource = dataSources.find(ds => ds.tableName === config.dataSource)
    if (dynamicSource) {
      return dynamicSource.fields
    }

    return getAllFields(config.dataSource)
  }, [dataSources, config.dataSource, cqSig])



  

  let numericFields = allFields.filter(f => f.type === 'number') 
  let categoricalFields = allFields.filter(f => f.type === 'string')

  if(numericFields.length == 0){
    numericFields = allFields;
  }

  if(categoricalFields.length == 0){
    categoricalFields = allFields;
  }

  

  const isLineChart = config.componentType === 'line-chart'
  const isBarChart = config.componentType === 'bar-chart'

  const isValid = Boolean(
    config.metadata?.title &&
    config.chartConfig?.xAxisKey &&
    config.chartConfig?.yAxisKey
    
  )


  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver
      </button>

      <h2 className="text-2xl font-bold mb-6">
        Configurar Gráfico {isLineChart ? 'de Líneas' : 'de Barras'}
      </h2>

      <div className="space-y-6 bg-white border rounded-lg p-6">


        <div>
          {/* Botón o trigger para mostrar los campos */}
          <button
            onClick={() => setShowTableFields(true)}
            disabled={!config.dataSource}
            className="px-6 py-2 text-sm rounded-lg whitespace-nowrap transition-all border bg-blue-600 text-white border-blue-600 shadow-sm"
          >
            Ver campos disponibles para fuente de datos : <b>{actualDataSource}</b>
          </button>

          {/* Modal de campos (incluye fuentes BMP) */}
           {showTableFields && actualDataSource && ( 
              <TableFieldsModal
                dataSource={actualDataSource}
                componentType={config.componentType}
                dataSources={dataSources}
                onClose={() => setShowTableFields(false)}
              />
            )}
        </div>
        {/* Título del gráfico */}
        <div>
          <label className="block font-medium mb-2">
            Título del Gráfico <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Ej: Evolución del Volumen Forestal"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.metadata?.title || ''}
            onChange={(e) =>
              onChange({
                ...config,
                metadata: { ...config.metadata, title: e.target.value },
              })
            }
          />
        </div>

        {/* Campo para eje X */}
        <div>
          <label className="block font-medium mb-2">
            Eje X (Horizontal) <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.chartConfig?.xAxisKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                chartConfig: {
                  ...config.chartConfig,
                  xAxisKey: e.target.value,
                },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {categoricalFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Campo para categorías o etiquetas (ej: municipios, especies, años)
          </p>
        </div>

        {/* Campo para eje Y */}
        <div>
          <label className="block font-medium mb-2">
            Eje Y (Vertical) <span className="text-red-500">*</span> 
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.chartConfig?.yAxisKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                chartConfig: {
                  ...config.chartConfig,
                  yAxisKey: e.target.value,
                },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {numericFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Campo numérico para valores (ej: volumen, superficie, cantidad)
          </p>
        </div>

        {/* Unidad para eje Y */}
        <div>
          <label className="block font-medium mb-2">Unidad de Medida (Eje Y)</label>
          <input
            type="text"
            placeholder="Ej: m³, ha, unidades"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.styling?.unit || ''}
            onChange={(e) =>
              onChange({
                ...config,
                styling: { ...config.styling, unit: e.target.value },
              })
            }
          />
        </div>

        {/* Color del gráfico */}
        <div>
          <label className="block font-medium mb-2">Color</label>
          <div className="flex gap-3">
            {[
              { value: '#10b981', label: 'Verde', bg: 'bg-green-500' },
              { value: '#3b82f6', label: 'Azul', bg: 'bg-blue-500' },
              { value: '#f59e0b', label: 'Naranja', bg: 'bg-orange-500' },
              { value: '#ef4444', label: 'Rojo', bg: 'bg-red-500' },
              { value: '#8b5cf6', label: 'Morado', bg: 'bg-purple-500' },
            ].map((color) => (
              <button
                key={color.value}
                type="button"
                className={`
                  ${color.bg} text-white px-4 py-2 rounded font-medium transition-all
                  ${
                    config.chartConfig?.chartColor === color.value
                      ? 'ring-4 ring-offset-2 ring-green-500'
                      : 'opacity-70 hover:opacity-100'
                  }
                `}
                onClick={() =>
                  onChange({
                    ...config,
                    chartConfig: {
                      ...config.chartConfig,
                      chartColor: color.value,
                    },
                  })
                }
              >
                {color.label}
              </button>
            ))}
          </div>
        </div>

        {/* Orientación (solo para gráficos de barras) */}
        {isBarChart && (
          <div>
            <label className="block font-medium mb-2">Orientación</label>
            <div className="flex gap-3">
              <button
                type="button"
                className={`
                  flex-1 border-2 rounded px-4 py-3 font-medium transition-all
                  ${
                    !config.chartConfig?.horizontal
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
                onClick={() =>
                  onChange({
                    ...config,
                    chartConfig: {
                      ...config.chartConfig,
                      horizontal: false,
                    },
                  })
                }
              >
                📊 Vertical
              </button>
              <button
                type="button"
                className={`
                  flex-1 border-2 rounded px-4 py-3 font-medium transition-all
                  ${
                    config.chartConfig?.horizontal
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
                onClick={() =>
                  onChange({
                    ...config,
                    chartConfig: {
                      ...config.chartConfig,
                      horizontal: true,
                    },
                  })
                }
              >
                📊 Horizontal
              </button>
            </div>
          </div>
        )}

        {/* Botón continuar */}
        <button
          onClick={onNext}
          disabled={!isValid}
          className="w-full bg-green-600 text-white py-3 rounded font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed mt-6"
        >
          Continuar a Vista Previa →
        </button>
      </div>
    </div>
  )
}